import useAttachments from "./useAttachments";
import { Trash2, UsersRound, CircleSlash, Check } from "lucide-react";
import { Dialog, Loader, FormSectionHeader } from "@/components";
import { images } from "@/assets";
import { Button, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Textarea, Popover, PopoverContent, PopoverTrigger} from "@/components/ui";
import { approvedTabs, fakeTabs, tabs } from "./consts";
import { cn, truncateByWords } from "@/lib/utils";
import moment from "moment";

const Attachments = () => {
  const { 
      isAttachmentsLoading,
      isView,
      attachmentsByRole, activeTab,
      onChangeTab,
      onDeleteAttachment,
      openDeleteAttachmentDialog,
      setOpenDeleteAttachmentDialog,
      onDeleteAttachmentConfirm,
      isDeletePending,
      isReviewPending,
      showConfirmation,
      setShowConfirmation,
      actionType,
      reason,
      setReason,
      onShowReviewConfirmation,
      handleReviewConfirm,
      previewPhotoUrl,
      onPreviewPhoto,
      closePreview,
      isPlanApproved
   } = useAttachments();

  return isAttachmentsLoading ? <Loader /> : (
    <>
      <div className="pt-1 px-2">
        <FormSectionHeader className="font-[600]">
          Attachments
        </FormSectionHeader>
        <Tabs value={activeTab} defaultValue="task-planning">
          <TabsList className="bg-transparent w-full">
            {[...tabs, ...isPlanApproved ? approvedTabs : fakeTabs]?.map((data: any, index: number) => (
              <TabsTrigger
                key={data?.value}
                value={data?.value}
                onClick={() => onChangeTab(data.value)}
                className="font-[12px]"
                disabled={!isPlanApproved && index != 0}
              >
                {data?.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {/* <div className="h-[0.5px] w-[100%] bg-gray-200 "/> */}
          <div className={cn("max-h-[calc(100vh-300px)] overflow-y-scroll")}>
            <TabsContent value={activeTab}>
              <div className="grid grid-cols-1 gap-[5px] min-h-[400px] overflow-y-scroll pb-10 no-scrollbar">
                {activeTab != "pm" ? attachmentsByRole?.length == 0 ? <></> : 
                  <div className="bg-primary-100 flex flex-row gap-2 mb-4 text-brown-300 w-fit h-fit p-1 font-[600] border-primary border-1 rounded-[6px] text-[12px] items-center">
                    <UsersRound size={10} /> Show it to customer
                  </div> : <></>}
                <div className="grid grid-cols-2  gap-y-4 gap-x-8 pr-6">
                  {attachmentsByRole?.length == 0 ?
                    <div className="flex flex-col items-center  justify-center w-full col-span-2">
                      <img src={images.no_data} className="w-[200px] h-auto" />
                    </div> :
                    attachmentsByRole?.map((data: any, index: number) => (
                      activeTab === "sm" ? (
                          <div
                            key={`key-attachment-${index}`}
                            className="bg-brown-400 rounded-[10px] text-white min-h-[220px] w-full flex flex-col relative overflow-visible shadow-lg"
                          >
                            <div 
                              className="relative h-[130px] w-full cursor-pointer"
                              onClick={() => onPreviewPhoto(data?.presignedUrl)}
                            >
                              <img
                                src={data?.presignedUrl || images.no_data}
                                className="h-full w-full object-cover rounded-t-[10px]"
                              />
                            </div>
                              {/* {isView ? <></> : ( */}
                                <span onClick={() => onDeleteAttachment(data)} className="cursor-pointer absolute -right-7 top-1">
                                  <Trash2 className="stroke-red-600 h-5 w-5" />
                                </span>
                              {/* )} */}
                          

                          <div className="p-2 flex flex-col gap-3">
                            <div className="flex justify-between items-start gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger type="button">
                                    <p className="text-[12px] font-medium leading-[16px] line-clamp-2 text-start">
                                      {truncateByWords(data?.description, 10) || "No description"}
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent className="mx-4 max-w-[200px] text-wrap break-all">
                                    <p>{data?.description || "No description"}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <div className="flex items-center gap-2">
                                {(data?.approvalStatus === "REJECT" || data?.approvalStatus === "REJECTED") && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <CircleSlash className="h-4 w-4 text-red-400 cursor-pointer" />
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[300px] bg-red-200 border-none rounded-lg p-4 text-gray-900 shadow-xl">
                                      <div className="flex flex-col gap-2 text-[13px] font-medium leading-[1.4]">
                                        <p>Rejected By : {data?.reviewedByName || "Admin"} at {data?.reviewedAt ? moment(data?.reviewedAt).format('h:mm a') : 'N/A'}</p>
                                        <p>Reason : {data?.rejectionReason || "No reason provided"}</p>
                                      </div>
                                    </PopoverContent>
                                  </Popover>
                                )}
                                {(data?.approvalStatus === "APPROVED" || data?.approvalStatus === "APPROVE") && (
                                  <div>
                                    <Check className="h-3 w-3 text-green-600" />
                                  </div>
                                )}
                                {data?.showToCustomer && <UsersRound className="h-4 w-4 text-yellow-500 shrink-0" />}
                              </div>
                            </div>

                            <div className="text-[10px] opacity-80 italic">
                              Uploaded By - {data?.createdByName || ""} at {moment(data?.createdAt).format('MM-DD-YYYY h:mm a')}
                            </div>
                            {data?.showToCustomer && (
                            <div className="flex gap-1 mt-auto">
                              <Button
                                variant="noHover"
                                onClick={() => onShowReviewConfirmation(data, "rejected")}
                                className="flex-1 h-8 bg-red-800 text-white border-none rounded-md text-[10px] gap-1 p-0"
                                disabled={(data.approvalStatus && data.approvalStatus !== "PENDING") || !data?.showToCustomer}
                              >
                                <CircleSlash className="h-0 w-0" />
                                Reject
                              </Button>
                              <Button
                                variant="noHover"
                                onClick={() => onShowReviewConfirmation(data, "approve")}
                                className="flex-1 h-8 bg-green-700 text-white border-none rounded-md text-[10px] gap-0 p-0"
                                disabled={(data.approvalStatus && data.approvalStatus !== "PENDING") || !data?.showToCustomer}
                              >
                                <Check className="h-0 w-0" />
                                Approve
                              </Button>
                            </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={`key-attachment-${index}`}
                          className="bg-brown-400 rounded-[6px] text-white h-[210px] w-[160px] flex flex-col relative"
                        >
                          <div className="flex flex-col">
                            <img
                              src={data?.presignedUrl || images.no_data}
                              className="h-[110px] w-[160px] rounded-[6px] "
                            />
                          </div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <div className="px-4 pt-1 w-full text-[10px] text-wrap break-all line-clamp-2 text-start">
                                  {truncateByWords(data?.description, 10) || ""}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="mx-4 max-w-[200px] text-wrap break-all">
                                <p>{data?.description || ""}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <div className="px-4 pt-1 w-full text-[10px] italic font-semibold text-wrap break-all">
                            Uploaded By - {data?.createdByName || ""} at {moment(data?.createdAt).format('MM-DD-YYYY h:mm a')}
                          </div>
                          {data?.showToCustomer ? (
                              <div className="flex justify-end text-primary absolute bottom-2 right-2">
                                <UsersRound className="h-4" />
                              </div>
                            ) : (
                              <></>
                            )}
                            {isView ? <></> : <span onClick={() => onDeleteAttachment(data)} className="cursor-pointer">
                              <Trash2 className="stroke-red-600 absolute top-0 -right-7 h-4" />
                            </span>}
                        </div>
                      )
                  ))}
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>
        <Dialog
          open={openDeleteAttachmentDialog}
          setOpen={setOpenDeleteAttachmentDialog}
          title={"Delete Attachment"}
          onSave={onDeleteAttachmentConfirm}
          saveText="Delete"
          saveBtnClassName="bg-red-600 text-white hover:bg-red-600 hover:text-white"
          isLoading={isDeletePending}
        >
          <p>
            Are you sure you want to delete this attachment?
          </p>
        </Dialog>
        <Dialog
          open={!!previewPhotoUrl}
          setOpen={closePreview}
          title={"Image Preview"}
          hideFooter={true}
          contentClassName="sm:max-w-[70vw]"
          childrenClassName="p-0 bg-black/5"
        >
          <div className="flex items-center justify-center">
            <img 
              src={previewPhotoUrl || ""} 
              className="max-w-full max-h-[80vh] object-contain" 
              alt="Preview"
            />
          </div>
        </Dialog>

        {showConfirmation && (
          <Dialog
            open={showConfirmation}
            setOpen={setShowConfirmation}
            title={actionType === "approve"
              ? "Approval Confirmation !"
              : "Reject Confirmation !"}
            onSave={handleReviewConfirm}
            onCancel={() => setShowConfirmation(false)}
            saveText={actionType === "approve"
              ? "Yes, Approve"
              : "Yes, Reject"}
            cancelText="No"
            saveBtnClassName="h-11 rounded-xl bg-primary px-8 text-[15px] text-dialog-title hover:bg-primary-hover cursor-pointer"
            cancelBtnClassName="h-11 rounded-xl border-dialog-btn-no-border px-10 text-[15px] text-dialog-btn-no-text hover:bg-dialog-no-hover hover:text-dialog-btn-no-text cursor-pointer"
            contentClassName="sm:max-w-[550px]"
            isLoading={isReviewPending}
          >
            <div className="pt-4 pb-2">
              <p className="text-[17px] text-dialog-message font-medium leading-[26px]">
                {actionType === "approve"
                  ? "Are you sure you want to approve this attachment?"
                  : "Are you sure you want to reject this image?"}
              </p>

              {actionType === "rejected" && (
                <div className="relative mt-10">
                  <label className="absolute -top-[11px] left-4 z-10 bg-white px-2 text-[13px] font-medium text-dialog-label">
                    Reason
                  </label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for rejection"
                    className="min-h-[100px] w-full resize-none rounded-xl border border-dialog-border bg-white p-4 text-[15px] placeholder:text-dialog-label focus:border-primary focus:ring-0 text-black"
                  />
                </div>
              )}
            </div>
          </Dialog>
        )}
      </div>
    </>
  )
};

export default Attachments;
